import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useCallback } from 'react';

import { tutorUsageFiltersAtom } from '../store/atoms/tutorUsageFiltersAtom';
import type { TutorUsageFilters } from '../types/tutorAi';

/**
 * Hook de acesso aos filtros do dashboard de uso e custos.
 *
 * @returns Filtros ativos, atualizadores por campo e reset para o default
 *
 * @example
 * ```tsx
 * const { filters, setClassStudent, reset } = useTutorUsageFilters();
 * setClassStudent('9020', 'DS-2026-1');
 * ```
 */
export function useTutorUsageFilters() {
  const [filters, setFilters] = useAtom(tutorUsageFiltersAtom);
  const resetFilters = useResetAtom(tutorUsageFiltersAtom);

  const patch = useCallback(
    (partial: Partial<TutorUsageFilters>) => {
      setFilters(current => ({ ...current, ...partial }));
    },
    [setFilters],
  );

  const setDateStart = useCallback((dateStart: string) => patch({ dateStart }), [patch]);
  const setDateEnd = useCallback((dateEnd: string) => patch({ dateEnd }), [patch]);

  const setClassStudent = useCallback(
    (classStudentId: string, classStudentName: string) =>
      patch({ classStudentId, classStudentName }),
    [patch],
  );

  return {
    filters,
    setDateStart,
    setDateEnd,
    setClassStudent,
    reset: resetFilters,
  } as const;
}
